import Keycloak from 'keycloak-js';
import axios from './axios';
import { KEYCLOAK_CONFIG } from '../configs/auth.config';

// Initialize Keycloak instance
const keycloakInstance = new Keycloak(KEYCLOAK_CONFIG);
let isInitialized = false;

// Expose the instance to the axios interceptor (which is loaded before this
// module). The interceptor reads window.__henddu_keycloak__ on every request
// to fetch a fresh token via keycloak.updateToken(30).
if (typeof window !== 'undefined') {
  window.__henddu_keycloak__ = keycloakInstance;
}

/**
 * Check if the current route is a public route that doesn't require authentication
 */
const isPublicRoute = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname.startsWith('/website');
  }
  return false;
};

/**
 * Initializes the Keycloak instance and attempts to authenticate
 * @param {Function} onSuccess - Callback when auth is successful
 * @param {Function} onError - Callback when auth fails
 * @returns {Promise} - Promise that resolves when initialization is complete
 */
const initKeycloak = (onSuccess, onError) => {
  if (isInitialized) {
    return Promise.resolve(keycloakInstance.authenticated);
  }
  isInitialized = true;

  // Skip authentication for public routes
  if (isPublicRoute()) {
    onSuccess && onSuccess(false);
    return Promise.resolve(false);
  }

  return keycloakInstance
    .init({ onLoad: 'login-required', checkLoginIframe: false })
    .then((authenticated) => {
      if (authenticated) {
        onSuccess && onSuccess(authenticated);
      } else {
        onError && onError();
      }
      return authenticated;
    })
    .catch((error) => {
      console.error('Keycloak init error:', error);
      onError && onError(error);
    });
};

/**
 * Logs the user in using Keycloak's login page
 */
const doLogin = () => {
  keycloakInstance.login();
};

/**
 * Logs the user out
 */
const doLogout = () => {
  // Clear admin tenant selection so it doesn't leak into the next session
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem('henddu_admin_selected_tenant');
    } catch {
      // ignore storage errors
    }
  }
  keycloakInstance.logout();
};

/**
 * Refreshes the token if it's valid.
 *
 * Note: regular API calls don't need to call this — the axios request
 * interceptor in utils/axios.js calls keycloak.updateToken(30) before
 * every request. This helper is only useful for code paths that read
 * keycloakInstance.token directly.
 */
const refreshToken = () => {
  return keycloakInstance.updateToken(5);
};

/**
 * Checks if the user is authenticated
 * @returns {boolean} - Whether the user is authenticated
 */
const isAuthenticated = () => {
  return !!keycloakInstance.authenticated;
};

/**
 * Gets the user profile from Keycloak
 * @returns {Object} - User profile information
 */
const getUserProfile = () => {
  if (!keycloakInstance.authenticated) return null;
  // Get avatar URL from Keycloak
  const avatarUrl = `${KEYCLOAK_CONFIG.url}/resources/yudm3/admin/henddu/img_avatar.svg`;

  return {
    id: keycloakInstance.subject,
    username: keycloakInstance.tokenParsed.preferred_username,
    email: keycloakInstance.tokenParsed.email,
    firstName: keycloakInstance.tokenParsed.given_name,
    lastName: keycloakInstance.tokenParsed.family_name,
    roles: keycloakInstance.tokenParsed.realm_access?.roles || [],
    tenantId: keycloakInstance.tokenParsed.tenant_id ?? null,
    picture: keycloakInstance.tokenParsed.picture || avatarUrl,
  };
};

/**
 * Syncs the user with the backend database.
 * This creates the user if they don't exist, or updates their profile
 * with data from the Keycloak token.
 *
 * @returns {Promise<Object>} - The synced user data from backend (normalized to camelCase)
 * @throws {Error} - If not authenticated or sync fails
 */
const syncUser = async () => {
  if (!keycloakInstance.authenticated) {
    throw new Error('Not authenticated');
  }

  const { data } = await axios.post('/api/auth/sync');

  if (!data?.success) {
    // Surface the ResponseBuilder error code so the auth context can
    // distinguish EX_TENANT_NOT_ASSIGNED (1010) from other failures.
    const err = new Error(data?.message || 'User sync failed');
    err.code = data?.code ?? null;
    throw err;
  }

  const backendUser = data.data.user;

  // Normalize backend response (snake_case) to frontend format (camelCase)
  // Also add username from Keycloak token for consistency
  return {
    id: backendUser.id,
    keycloakId: backendUser.keycloak_id,
    email: backendUser.email,
    name: backendUser.name?.trim() || '',
    firstName: backendUser.first_name?.trim() || '',
    lastName: backendUser.last_name?.trim() || '',
    username:
      keycloakInstance.tokenParsed?.preferred_username || backendUser.email,
    roles: backendUser.roles || [],
    tenantId: backendUser.tenant_id || null,
    avatarUrl: backendUser.avatar_url,
    timezone: backendUser.timezone,
    language: backendUser.language,
    lastLoginAt: backendUser.last_login_at,
  };
};

/**
 * Gets the current authenticated user from backend.
 * Unlike getUserProfile(), this returns the full user record from the database.
 *
 * @returns {Promise<Object>} - The user data from backend
 * @throws {Error} - If not authenticated or request fails
 */
const getCurrentUser = async () => {
  if (!keycloakInstance.authenticated) {
    throw new Error('Not authenticated');
  }

  const { data } = await axios.get('/api/auth/me');

  if (!data?.success) {
    throw new Error(data?.message || 'Failed to get current user');
  }

  return data.data.user;
};

/**
 * Gets the Keycloak token
 * @returns {string} - The current token
 */
const getToken = () => keycloakInstance.token;

/**
 * Gets the Keycloak instance
 * @returns {Keycloak} - The Keycloak instance
 */
const getKeycloakInstance = () => keycloakInstance;

export {
  initKeycloak,
  doLogin,
  doLogout,
  refreshToken,
  isAuthenticated,
  getUserProfile,
  syncUser,
  getCurrentUser,
  getToken,
  getKeycloakInstance,
};
